import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import DotsLoading from '../ui/DotsLoading';
import { Clock, Calendar, Settings, Plus, Trash2, Save, X, CheckCircle, AlertCircle } from 'lucide-react';

const Form = styled.form`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  
  @media (max-width: 768px) {
    gap: 20px;
  }
  
  @media (max-width: 480px) {
    gap: 16px;
  }
`;

const FormCard = styled.div`
  background: #25253C;
  border-radius: 16px;
  padding: 24px;
  backdrop-filter: blur(10px);
  
  @media (max-width: 768px) {
    padding: 16px;
    border-radius: 12px;
  }
  
  @media (max-width: 480px) {
    padding: 16px;
    border-radius: 10px;
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  
  @media (max-width: 768px) {
    margin-bottom: 20px;
    padding-bottom: 12px;
    gap: 10px;
  }
  
  @media (max-width: 480px) {
    margin-bottom: 16px;
    padding-bottom: 8px;
    gap: 8px;
  }
`;

const CardTitle = styled.h3`
  color: #A1A1B5;
  font-size: 16px;
  font-weight: 500;
  margin: 0;
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
  
  @media (max-width: 480px) {
    font-size: 16px;
  }
`;

const CardIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #F43F60, #E03A56);
  border-radius: 12px;
  color: #fff;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 24px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
  
  @media (max-width: 480px) {
    gap: 16px;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  
  @media (max-width: 480px) {
    gap: 6px;
  }
`;

const Label = styled.label`
  color: #ffffff;
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 6px;
  
  @media (max-width: 480px) {
    font-size: 13px;
    gap: 4px;
  }
`;

const Input = styled.input`
  width: 100%;
  height: 48px;
  background: #1E1E35;
  border: 1px solid #1E1E35;
  border-radius: 12px;
  padding: 0 16px;
  color: #ffffff;
  font-size: 14px;
  outline: none;
  transition: all 0.3s ease;
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
  
  &:focus {
    border-color: #999;
    background: #1E1E35;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  /* Custom styling for date input */
  &[type="date"]::-webkit-calendar-picker-indicator {
    filter: invert(1) brightness(0.8);
    cursor: pointer;
    transition: all 0.3s ease;
  }
  
  &[type="date"]::-webkit-calendar-picker-indicator:hover {
    filter: invert(1) brightness(1) sepia(1) saturate(5) hue-rotate(320deg);
  }
  
  @media (max-width: 768px) {
    height: 44px;
    padding: 0 14px;
    font-size: 13px;
    border-radius: 10px;
  }
  
  @media (max-width: 480px) {
    height: 40px;
    padding: 0 12px;
    font-size: 12px;
    border-radius: 8px;
  }
`;

const TimeInput = styled.input`
  width: 100%;
  height: 48px;
  background: #1E1E35;
  border: 1px solid #1E1E35;
  border-radius: 12px;
  padding: 0 16px;
  color: #ffffff;
  font-size: 14px;
  outline: none;
  transition: all 0.3s ease;
  
  &:focus {
    border-color: #999;
    background: #1E1E35;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  /* Custom styling for time input */
  &::-webkit-calendar-picker-indicator {
    filter: invert(1) brightness(0.8);
    cursor: pointer;
    transition: all 0.3s ease;
  }
  
  &::-webkit-calendar-picker-indicator:hover {
    filter: invert(1) brightness(1) sepia(1) saturate(5) hue-rotate(320deg);
  }
  
  @media (max-width: 768px) {
    height: 44px;
    padding: 0 14px;
    font-size: 13px;
    border-radius: 10px;
  }
  
  @media (max-width: 480px) {
    height: 40px;
    padding: 0 12px;
    font-size: 12px;
    border-radius: 8px;
  }
`;

const NumberInput = styled.input`
  width: 100%;
  height: 48px;
  background: #1E1E35;
  border: 1px solid #1E1E35;
  border-radius: 12px;
  padding: 0 16px;
  color: #ffffff;
  font-size: 14px;
  outline: none;
  transition: all 0.3s ease;
  
  &:focus {
    border-color: #999;
    background: #1E1E35;
  }
  
  /* Prevent wheel scrolling from changing values */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  
  &[type=number] {
    -moz-appearance: textfield;
  }
  
  @media (max-width: 768px) {
    height: 44px;
    padding: 0 14px;
    font-size: 13px;
    border-radius: 10px;
  }
  
  @media (max-width: 480px) {
    height: 40px;
    padding: 0 12px;
    font-size: 12px;
    border-radius: 8px;
  }
`;

const WorkingDayCard = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  padding: 20px;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(255, 255, 255, 0.12);
  }
  
  @media (max-width: 768px) {
    padding: 16px;
    border-radius: 10px;
  }
  
  @media (max-width: 480px) {
    padding: 14px;
    border-radius: 8px;
  }
`;

const DayHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  
  @media (max-width: 480px) {
    margin-bottom: 12px;
  }
`;

const DayTitle = styled.h4`
  color: #ffffff;
  font-size: 16px;
  font-weight: 600;
  margin: 0;
  
  @media (max-width: 480px) {
    font-size: 14px;
  }
`;

const WorkingToggle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ToggleLabel = styled.span`
  color: rgba(255, 255, 255, 0.7);
  font-size: 12px;
  font-weight: 500;
`;

const ToggleSwitch = styled.label`
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
  background: ${props => props.checked ? '#F43F60' : 'rgba(255, 255, 255, 0.2)'};
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:after {
    content: '';
    position: absolute;
    top: 2px;
    left: ${props => props.checked ? '22px' : '2px'};
    width: 20px;
    height: 20px;
    background: white;
    border-radius: 50%;
    transition: all 0.3s ease;
  }
  
  @media (max-width: 480px) {
    width: 40px;
    height: 22px;
    
    &:after {
      width: 18px;
      height: 18px;
      left: ${props => props.checked ? '20px' : '2px'};
    }
  }
`;

const ToggleInput = styled.input`
  opacity: 0;
  width: 0;
  height: 0;
`;

const TimeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 12px;
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 8px;
  }
`;

const TimeGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const TimeLabel = styled.span`
  color: rgba(255, 255, 255, 0.6);
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const TimeError = styled.div`
  color: #F43F60;
  font-size: 10px;
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
  
  &::before {
    content: "⚠";
    font-size: 8px;
  }
`;

const HolidayItem = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 12px;
  transition: all 0.3s ease;
  
  
  @media (max-width: 768px) {
    padding: 16px;
    border-radius: 10px;
  }
  
  @media (max-width: 480px) {
    padding: 14px;
    border-radius: 8px;
    margin-bottom: 8px;
  }
`;

const HolidayGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr auto;
  gap: 16px;
  align-items: end;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
  
  @media (max-width: 480px) {
    gap: 8px;
  }
`;

const SpecialHoursItem = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 12px;
  transition: all 0.3s ease;
  
  @media (max-width: 768px) {
    padding: 16px;
    border-radius: 10px;
  }
  
  @media (max-width: 480px) {
    padding: 14px;
    border-radius: 8px;
    margin-bottom: 8px;
  }
`;

const SpecialHoursGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr auto auto auto auto auto auto;
  gap: 12px;
  align-items: end;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr auto auto auto auto auto;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
  
  @media (max-width: 480px) {
    gap: 8px;
  }
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: #1E1E35;
  color: #ffffff;
  border-radius: 12px;
  padding: 12px 20px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 16px;

  @media (max-width: 768px) {
    padding: 10px 16px;
    font-size: 13px;
    margin-top: 12px;
    border-radius: 10px;
  }
  
  @media (max-width: 480px) {
    padding: 8px 14px;
    font-size: 12px;
    margin-top: 8px;
    border-radius: 8px;
  }
`;

const RemoveButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #F43F60, #E03A56);
  color: white;
  border: none;
  border-radius: 8px;
  width: 36px;
  height: 36px;
  cursor: pointer;
  transition: all 0.3s ease;

  @media (max-width: 768px) {
    width: 32px;
    height: 32px;
    border-radius: 6px;
  }
  
  @media (max-width: 480px) {
    width: 28px;
    height: 28px;
    border-radius: 4px;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 32px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
    margin-top: 24px;
  }
  
  @media (max-width: 480px) {
    gap: 8px;
    margin-top: 20px;
  }
`;

const PrimaryButton = styled.button`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: linear-gradient(135deg, #F43F60, #E03A56);
  color: white;
  border: none;
  border-radius: 12px;
  padding: 16px 24px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #E03A56, #D0354C);
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
  }
  
  &:disabled {
    background: linear-gradient(135deg, #666666, #555555);
    cursor: not-allowed;
    opacity: 0.7;
    transform: none;
    box-shadow: none;
  }
  
  @media (max-width: 768px) {
    padding: 14px 20px;
    font-size: 15px;
    border-radius: 10px;
  }
  
  @media (max-width: 480px) {
    padding: 12px 16px;
    font-size: 14px;
    border-radius: 8px;
  }
`;

const SecondaryButton = styled.button`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.08);
  color: #ffffff;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 12px;
  padding: 16px 24px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.12);
    border-color: rgba(255, 255, 255, 0.25);
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
  
  @media (max-width: 768px) {
    padding: 14px 20px;
    font-size: 15px;
    border-radius: 10px;
  }
  
  @media (max-width: 480px) {
    padding: 12px 16px;
    font-size: 14px;
    border-radius: 8px;
  }
`;

const MessageContainer = styled.div`
  margin-bottom: 24px;
  
  @media (max-width: 768px) {
    margin-bottom: 20px;
  }
  
  @media (max-width: 480px) {
    margin-bottom: 16px;
  }
`;

const ErrorMessage = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  background: rgba(244, 63, 96, 0.1);
  border: 1px solid rgba(244, 63, 96, 0.3);
  color: #F43F60;
  padding: 16px 20px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 500;
  
  @media (max-width: 768px) {
    padding: 14px 16px;
    font-size: 13px;
    border-radius: 10px;
    gap: 10px;
  }
  
  @media (max-width: 480px) {
    padding: 12px 14px;
    font-size: 12px;
    border-radius: 8px;
    gap: 8px;
  }
`;

const SuccessMessage = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  background: rgba(34, 197, 94, 0.1);
  border: 1px solid rgba(34, 197, 94, 0.3);
  color: #22C55E;
  padding: 16px 20px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 500;
  
  @media (max-width: 768px) {
    padding: 14px 16px;
    font-size: 13px;
    border-radius: 10px;
    gap: 10px;
  }
  
  @media (max-width: 480px) {
    padding: 12px 14px;
    font-size: 12px;
    border-radius: 8px;
    gap: 8px;
  }
`;

// Global styles for date/time pickers
const GlobalStyles = styled.div`
  /* Date picker styling */
  input[type="date"]::-webkit-datetime-edit {
    color: #ffffff;
  }
  
  input[type="date"]::-webkit-datetime-edit-fields-wrapper {
    color: #ffffff;
  }
  
  input[type="date"]::-webkit-datetime-edit-text {
    color: rgba(255, 255, 255, 0.7);
  }
  
  input[type="date"]::-webkit-datetime-edit-month-field,
  input[type="date"]::-webkit-datetime-edit-day-field,
  input[type="date"]::-webkit-datetime-edit-year-field {
    color: #ffffff;
  }
  
  input[type="date"]::-webkit-inner-spin-button,
  input[type="date"]::-webkit-outer-spin-button {
    display: none;
  }
  
  /* Time picker styling */
  input[type="time"]::-webkit-datetime-edit {
    color: #ffffff;
  }
  
  input[type="time"]::-webkit-datetime-edit-fields-wrapper {
    color: #ffffff;
  }
  
  input[type="time"]::-webkit-datetime-edit-text {
    color: rgba(255, 255, 255, 0.7);
  }
  
  input[type="time"]::-webkit-datetime-edit-hour-field,
  input[type="time"]::-webkit-datetime-edit-minute-field {
    color: #ffffff;
  }
  
  input[type="time"]::-webkit-inner-spin-button,
  input[type="time"]::-webkit-outer-spin-button {
    display: none;
  }
  
  /* Focus states for date/time inputs */
  input[type="date"]:focus::-webkit-datetime-edit,
  input[type="time"]:focus::-webkit-datetime-edit {
    color: #ffffff;
  }
  
  input[type="date"]:focus::-webkit-datetime-edit-fields-wrapper,
  input[type="time"]:focus::-webkit-datetime-edit-fields-wrapper {
    color: #ffffff;
  }
`;

const UpdateScheduleForm = ({ values: initialValues, onSubmit, isSubmitting, error, success }) => {
  const [values, setValues] = useState(initialValues);
  const [isDirty, setIsDirty] = useState(false);
  const [timeErrors, setTimeErrors] = useState({});

  useEffect(() => {
    setValues(initialValues);
  }, [initialValues]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setValues(prev => ({ ...prev, [name]: value }));
    setIsDirty(true);
  };

  const handleNumberInputWheel = (e) => {
    // Prevent wheel scrolling from changing number input values
    e.target.blur();
  };

  // Convert time string to minutes for comparison
  const timeToMinutes = (timeStr) => {
    if (!timeStr) return 0;
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + minutes;
  };

  // Validate time relationships for a specific day
  const validateDayTimes = (day) => {
    const dayData = values.workingHours[day];
    if (!dayData.isWorking) return null;

    const startMinutes = timeToMinutes(dayData.start);
    const endMinutes = timeToMinutes(dayData.end);
    const breakStartMinutes = timeToMinutes(dayData.breakStart);
    const breakEndMinutes = timeToMinutes(dayData.breakEnd);

    if (startMinutes >= endMinutes) {
      return 'Start time must be before end time';
    }

    if (breakStartMinutes >= breakEndMinutes) {
      return 'Break start must be before break end';
    }

    if (breakStartMinutes < startMinutes) {
      return 'Break start must be after work start time';
    }

    if (breakEndMinutes > endMinutes) {
      return 'Break end must be before work end time';
    }

    return null;
  };

  // Validate special hours times
  const validateSpecialHours = (special) => {
    if (!special.isWorking) return null;

    const startMinutes = timeToMinutes(special.start);
    const endMinutes = timeToMinutes(special.end);
    const breakStartMinutes = timeToMinutes(special.breakStart);
    const breakEndMinutes = timeToMinutes(special.breakEnd);

    if (startMinutes >= endMinutes) {
      return 'Start time must be before end time';
    }

    if (breakStartMinutes >= breakEndMinutes) {
      return 'Break start must be before break end';
    }

    if (breakStartMinutes < startMinutes) {
      return 'Break start must be after work start time';
    }

    if (breakEndMinutes > endMinutes) {
      return 'Break end must be before work end time';
    }

    return null;
  };

  const handleWorkingHoursChange = (day, field, value) => {
    setValues(prev => ({
      ...prev,
      workingHours: {
        ...prev.workingHours,
        [day]: {
          ...prev.workingHours[day],
          [field]: value
        }
      }
    }));
    setIsDirty(true);

    // Validate times after a short delay to allow state update
    setTimeout(() => {
      const error = validateDayTimes(day);
      setTimeErrors(prev => ({
        ...prev,
        [day]: error
      }));
    }, 100);
  };

  const handleHolidayChange = (idx, field, value) => {
    setValues(prev => {
      const holidays = [...prev.holidays];
      holidays[idx] = { ...holidays[idx], [field]: value };
      return { ...prev, holidays };
    });
    setIsDirty(true);
  };

  const handleSpecialHoursChange = (idx, field, value) => {
    setValues(prev => {
      const specialHours = [...prev.specialHours];
      specialHours[idx] = { 
        ...specialHours[idx], 
        [field]: value 
      };
      return { ...prev, specialHours };
    });
    setIsDirty(true);

    // Validate times after a short delay to allow state update
    setTimeout(() => {
      const error = validateSpecialHours(values.specialHours[idx]);
      setTimeErrors(prev => ({
        ...prev,
        [`special_${idx}`]: error
      }));
    }, 100);
  };

  const handleAddHoliday = () => {
    setValues(prev => ({
      ...prev,
      holidays: [...prev.holidays, { date: '', reason: '' }]
    }));
    setIsDirty(true);
  };

  const handleAddSpecialHours = () => {
    setValues(prev => ({
      ...prev,
      specialHours: [...prev.specialHours, {
        date: '',
        isWorking: true,
        start: '09:00',
        end: '17:00',
        breakStart: '12:00',
        breakEnd: '13:00',
        reason: ''
      }]
    }));
    setIsDirty(true);
  };

  const handleRemoveHoliday = (idx) => {
    setValues(prev => ({
      ...prev,
      holidays: prev.holidays.filter((_, i) => i !== idx)
    }));
    setIsDirty(true);
  };

  const handleRemoveSpecialHours = (idx) => {
    setValues(prev => ({
      ...prev,
      specialHours: prev.specialHours.filter((_, i) => i !== idx)
    }));
    setIsDirty(true);
  };

  const handleCancel = () => {
    setValues(initialValues);
    setIsDirty(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate all working days
    const allErrors = {};
    days.forEach(day => {
      const error = validateDayTimes(day);
      if (error) {
        allErrors[day] = error;
      }
    });

    // Validate special hours
    values.specialHours.forEach((special, idx) => {
      const error = validateSpecialHours(special);
      if (error) {
        allErrors[`special_${idx}`] = error;
      }
    });

    setTimeErrors(allErrors);

    // If there are time errors, don't submit
    if (Object.keys(allErrors).length > 0) {
      return;
    }

    onSubmit(values, () => {
      setIsDirty(false);
      setTimeErrors({});
    });
  };

  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

  return (
    <GlobalStyles>
    <Form onSubmit={handleSubmit}>
      <FormContainer>
        {error && (
          <MessageContainer>
            <ErrorMessage>
              <AlertCircle size={20} />
              {error}
            </ErrorMessage>
          </MessageContainer>
        )}
        
        {success && (
          <MessageContainer>
            <SuccessMessage>
              <CheckCircle size={20} />
              {success}
            </SuccessMessage>
          </MessageContainer>
        )}

        <FormCard>
          <CardHeader>
            <CardIcon>
              <Settings size={20} />
            </CardIcon>
            <CardTitle>General Settings</CardTitle>
          </CardHeader>
          
          <FormGrid>
            <FormGroup>
              <Label>
                <Clock size={16} />
                Slot Duration (minutes)
              </Label>
              <NumberInput
                type="number"
                name="slotDuration"
                value={values.slotDuration}
                onChange={handleChange}
                onWheel={handleNumberInputWheel}
                min="15"
                max="120"
                step="15"
                placeholder="30"
                required
              />
            </FormGroup>
            
            <FormGroup>
              <Label>
                <Clock size={16} />
                Buffer Time (minutes)
              </Label>
              <NumberInput
                type="number"
                name="bufferTime"
                value={values.bufferTime}
                onChange={handleChange}
                onWheel={handleNumberInputWheel}
                min="0"
                max="60"
                step="5"
                placeholder="15"
                required
              />
            </FormGroup>
            
            <FormGroup>
              <Label>
                <Settings size={16} />
                Max Appointments Per Slot
              </Label>
              <NumberInput
                type="number"
                name="maxAppointmentsPerSlot"
                value={values.maxAppointmentsPerSlot}
                onChange={handleChange}
                onWheel={handleNumberInputWheel}
                min="1"
                max="10"
                placeholder="3"
                required
              />
            </FormGroup>
          </FormGrid>
        </FormCard>

        <FormCard>
          <CardHeader>
            <CardIcon>
              <Clock size={20} />
            </CardIcon>
            <CardTitle>Working Hours</CardTitle>
          </CardHeader>
          
          <FormGrid>
            {days.map(day => (
              <WorkingDayCard key={day}>
                <DayHeader>
                  <DayTitle>{day.charAt(0).toUpperCase() + day.slice(1)}</DayTitle>
                  <WorkingToggle>
                    <ToggleLabel>Working Day</ToggleLabel>
                    <ToggleSwitch checked={values.workingHours[day].isWorking}>
                      <ToggleInput
                        type="checkbox"
                        checked={values.workingHours[day].isWorking}
                        onChange={(e) => handleWorkingHoursChange(day, 'isWorking', e.target.checked)}
                      />
                    </ToggleSwitch>
                  </WorkingToggle>
                </DayHeader>
                
                <TimeGrid>
                  <TimeGroup>
                    <TimeLabel>Start Time</TimeLabel>
                    <TimeInput
                      type="time"
                      value={values.workingHours[day].start}
                      onChange={(e) => handleWorkingHoursChange(day, 'start', e.target.value)}
                      disabled={!values.workingHours[day].isWorking}
                    />
                  </TimeGroup>
                  
                  <TimeGroup>
                    <TimeLabel>End Time</TimeLabel>
                    <TimeInput
                      type="time"
                      value={values.workingHours[day].end}
                      onChange={(e) => handleWorkingHoursChange(day, 'end', e.target.value)}
                      disabled={!values.workingHours[day].isWorking}
                    />
                  </TimeGroup>
                  
                  <TimeGroup>
                    <TimeLabel>Break Start</TimeLabel>
                    <TimeInput
                      type="time"
                      value={values.workingHours[day].breakStart}
                      onChange={(e) => handleWorkingHoursChange(day, 'breakStart', e.target.value)}
                      disabled={!values.workingHours[day].isWorking}
                    />
                  </TimeGroup>
                  
                  <TimeGroup>
                    <TimeLabel>Break End</TimeLabel>
                    <TimeInput
                      type="time"
                      value={values.workingHours[day].breakEnd}
                      onChange={(e) => handleWorkingHoursChange(day, 'breakEnd', e.target.value)}
                      disabled={!values.workingHours[day].isWorking}
                    />
                  </TimeGroup>
                </TimeGrid>
                
                {timeErrors[day] && (
                  <TimeError>{timeErrors[day]}</TimeError>
                )}
              </WorkingDayCard>
            ))}
          </FormGrid>
        </FormCard>

        <FormCard>
          <CardHeader>
            <CardIcon>
              <Calendar size={20} />
            </CardIcon>
            <CardTitle>Holidays</CardTitle>
          </CardHeader>
          
          {values.holidays.map((holiday, idx) => (
            <HolidayItem key={idx}>
              <HolidayGrid>
                <FormGroup>
                  <Label>
                    <Calendar size={16} />
                    Date
                  </Label>
                  <Input
                    type="date"
                    value={holiday.date ? holiday.date.split('T')[0] : ''}
                    onChange={(e) => handleHolidayChange(idx, 'date', e.target.value)}
                    placeholder="Select date"
                    required
                  />
                </FormGroup>
                
                <FormGroup>
                  <Label>
                    <AlertCircle size={16} />
                    Reason
                  </Label>
                  <Input
                    type="text"
                    value={holiday.reason}
                    onChange={(e) => handleHolidayChange(idx, 'reason', e.target.value)}
                    placeholder="Holiday reason"
                    required
                  />
                </FormGroup>
                
                <RemoveButton type="button" onClick={() => handleRemoveHoliday(idx)}>
                  <Trash2 size={16} />
                </RemoveButton>
              </HolidayGrid>
            </HolidayItem>
          ))}
          
          <AddButton type="button" onClick={handleAddHoliday}>
            <Plus size={16} />
            Add Holiday
          </AddButton>
        </FormCard>

        <FormCard>
          <CardHeader>
            <CardIcon>
              <Clock size={20} />
            </CardIcon>
            <CardTitle>Special Hours</CardTitle>
          </CardHeader>
          
          {values.specialHours.map((special, idx) => (
            <SpecialHoursItem key={idx}>
              <SpecialHoursGrid>
                <FormGroup>
                  <Label>
                    <Calendar size={16} />
                    Date
                  </Label>
                  <Input
                    type="date"
                    value={special.date ? special.date.split('T')[0] : ''}
                    onChange={(e) => handleSpecialHoursChange(idx, 'date', e.target.value)}
                    placeholder="Select date"
                    required
                  />
                </FormGroup>
                
                <FormGroup>
                  <Label>
                    <Settings size={16} />
                    Working
                  </Label>
                  <ToggleSwitch checked={special.isWorking}>
                    <ToggleInput
                      type="checkbox"
                      checked={special.isWorking}
                      onChange={(e) => handleSpecialHoursChange(idx, 'isWorking', e.target.checked)}
                    />
                  </ToggleSwitch>
                </FormGroup>
                
                <FormGroup>
                  <Label>Start</Label>
                  <TimeInput
                    type="time"
                    value={special.start}
                    onChange={(e) => handleSpecialHoursChange(idx, 'start', e.target.value)}
                    disabled={!special.isWorking}
                  />
                </FormGroup>
                
                <FormGroup>
                  <Label>End</Label>
                  <TimeInput
                    type="time"
                    value={special.end}
                    onChange={(e) => handleSpecialHoursChange(idx, 'end', e.target.value)}
                    disabled={!special.isWorking}
                  />
                </FormGroup>
                
                <FormGroup>
                  <Label>Break Start</Label>
                  <TimeInput
                    type="time"
                    value={special.breakStart}
                    onChange={(e) => handleSpecialHoursChange(idx, 'breakStart', e.target.value)}
                    disabled={!special.isWorking}
                  />
                </FormGroup>
                
                <FormGroup>
                  <Label>Break End</Label>
                  <TimeInput
                    type="time"
                    value={special.breakEnd}
                    onChange={(e) => handleSpecialHoursChange(idx, 'breakEnd', e.target.value)}
                    disabled={!special.isWorking}
                  />
                </FormGroup>
                
                <RemoveButton type="button" onClick={() => handleRemoveSpecialHours(idx)}>
                  <Trash2 size={16} />
                </RemoveButton>
              </SpecialHoursGrid>
              
              {timeErrors[`special_${idx}`] && (
                <TimeError>{timeErrors[`special_${idx}`]}</TimeError>
              )}
            </SpecialHoursItem>
          ))}
          
          <AddButton type="button" onClick={handleAddSpecialHours}>
            <Plus size={16} />
            Add Special Hours
          </AddButton>
        </FormCard>

        <ButtonContainer>
          <SecondaryButton type="button" onClick={handleCancel} disabled={!isDirty || isSubmitting}>
            <X size={18} />
            Cancel
          </SecondaryButton>
          <PrimaryButton type="submit" disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <DotsLoading />
                Saving...
              </>
            ) : (
              <>
                <Save size={18} />
                Save Schedule
              </>
            )}
          </PrimaryButton>
          
        </ButtonContainer>
      </FormContainer>
    </Form>
    </GlobalStyles>
  );
};

export default UpdateScheduleForm; 